import React, { useEffect } from 'react'
import _ from 'underscore'
import styled from 'styled-components'

import { Panel } from 'components/Panel'
import { NoData } from 'styles/shared'
import ConnectedStatusIcon from 'pages/shared/ConnectedStatusIcon'
import LastUpdated from 'pages/Device/LastUpdated'
import EnrolledIcon from 'pages/shared/EnrolledIcon'

import Status from './Status'
import ThermostatBox from './ThermostatBox'
import ZoneBox from './ZoneBox'

import useDefaultFirmwareLoads from 'hooks/useDefaultFirmwareLoads'
import useNexiaStream from 'hooks/useNexiaStream'
import useServerlessDemandResponse from 'hooks/useServerlessDemandResponse'
import { useFeaturesContext } from 'hooks/FeaturesContext'
import { useWebsocketContext } from 'hooks/WebsocketContext'

import { color } from 'styles/colors'

import ExtendedDisconnect from 'pages/shared/Device/ExtendedDisconnect'

type Props = {
  deviceId: string
  hasAwairDevice: boolean
  reportCache: object
  onNoZones?: () => void
  deviceModel?: string
}

const DashboardStatus = ({ deviceId, hasAwairDevice, reportCache, onNoZones, deviceModel }: Props) => {
  const { eventData: report } = useNexiaStream(deviceId, 'current_status', reportCache)
  const { demandResponse } = useServerlessDemandResponse(deviceId, false)
  useDefaultFirmwareLoads()

  const { subscribe, unsubscribe, payload: wsReport } = useWebsocketContext(deviceId, 'system_status')
  const { enabledFeatures } = useFeaturesContext()
  const isWebsocketEnabled = enabledFeatures.includes('montblanc_websockets')

  useEffect(() => {
    if (isWebsocketEnabled) {
      subscribe(deviceId, 'system_status')
    }

    return () => {
      if (isWebsocketEnabled) {
        unsubscribe(deviceId, 'system_status')
      }
    }
  }, [deviceId, isWebsocketEnabled, subscribe, unsubscribe])

  const effectiveReport = isWebsocketEnabled ? wsReport?.systemStatus : report

  const extendedDisconnect = isWebsocketEnabled ? wsReport?.extendedDisconnect : false

  useEffect(() => {
    if (effectiveReport && onNoZones && (!effectiveReport.zones || effectiveReport.zones.length <= 1)) {
      onNoZones()
    }
  }, [effectiveReport, onNoZones])

  if (!effectiveReport) {
    return (
      <S.Panel>
        <Panel.Header>
          <Panel.Title>Status</Panel.Title>
        </Panel.Header>
        <S.NoData><h1>No Data Received Yet</h1></S.NoData>
      </S.Panel>
    )
  }

  if (extendedDisconnect) {
    return (<ExtendedDisconnect title="Status" type="preview" />)
  }

  if (!effectiveReport.zones || effectiveReport.zones.length === 0) {
    return null
  }

  return (
    <S.Panel>
      <Panel.Header>
        <Panel.Title>Status<S.ConnectedStatusIcon isConnected={effectiveReport.connected} size="14" /> </Panel.Title>
        <LastUpdated date={effectiveReport.lastUpdatedAt} />
      </Panel.Header>
      <S.Container>
        <Status report={effectiveReport} hasZones={effectiveReport.zones.length > 1} deviceId={deviceId} deviceModel={deviceModel} />
        {demandResponse && demandResponse.enrollmentStatus &&
          <EnrolledIcon showTooltip />
        }
      </S.Container>
      {
        !_.isEmpty(effectiveReport.zones) &&
        effectiveReport.zones.length === 1 &&
        <ThermostatBox {...effectiveReport.zones[0]} mode={effectiveReport.systemMode} outdoorTemperature={effectiveReport.outdoorTemperature} hasAwairDevice={hasAwairDevice} />
      }
      {
        effectiveReport.zones.length > 1 &&
          <S.ZoneHeader>
            <Panel.Title>Zones</Panel.Title>
            <LastUpdated date={effectiveReport.lastUpdatedAt} />
          </S.ZoneHeader>
      }
      {
        effectiveReport.zones.length > 1 && (effectiveReport.zones.map((zone, index) => {
          return (
            <ZoneBox key={`zonebox-${zone.zoneId || index}`} {...zone} mode={zone.reqZoneMode} hasAwairDevice={hasAwairDevice} />
          )
        }))
      }
    </S.Panel>
  )
}

export default DashboardStatus

const S = {
  Panel: styled(Panel)`
    background-color: ${color('paneBackground')};
    border-bottom: 0px;
    padding: 0px;
    width: calc(100% - 0px);
  `,
  NoData: styled(NoData)`
    h1 {
      font-size: 28px;
    }
  `,
  ConnectedStatusIcon: styled(ConnectedStatusIcon)`
    width: 16px;
    height: 16px;
  `,
  ZoneHeader: styled(Panel.Header)`
    border-top: 1px solid ${color('border')};
    border-radius: 0px;
    padding-top: 10px;
    margin-top: 15px;
  `,
  Container: styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  `
}
